// The forward action for a step.
//
// Two modes:
//
//   pinned (default, set by JourneyShell) — a bar fixed to the bottom of the
//     viewport. Its position is a property of the window, not of the form, so
//     it does not move when the step's content grows or shrinks and it stays
//     reachable without scrolling to the end of a long form. The body scrolls
//     underneath it; JourneyShell reserves padding equal to the measured bar
//     height (`onHeightChange`) so the last field can always clear it.
//
//   inline — the same action in the flow of the page, at the end of the
//     content. Kept for surfaces that should not carry a permanent bar, and
//     paired with the ribbon: `onInViewChange` tells the shell when this
//     footer is off screen so the ribbon can stand in for it.
//
// This is not the old full-width Back+Proceed pair. There is one action; the
// left side carries the step's progress ring, which is also the "take me to the
// next empty field" affordance; and the caption names where Continue leads,
// so the button answers "and then what?" before it is pressed.
import React, { useEffect, useRef } from "react";
import { Box, Button, CircularProgress, Grid2, Stack, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { styled } from "@mui/material/styles";
import StepProgress from "./StepProgress";
import { useInView } from "../../hooks/useInView";

const INK = "#3d322b";
const MUTED = "#7B6E66";

export const PrimaryAction = styled(Button)(() => ({
  background: "linear-gradient(135deg, #d3b48a 0%, #C9A777 50%, #b4966b 100%)",
  color: "#fff",
  fontWeight: 600,
  fontSize: "1rem",
  letterSpacing: 0.2,
  textTransform: "none",
  padding: "12px 28px",
  minHeight: 48,
  borderRadius: 999,
  boxShadow: "0 8px 18px rgba(201,167,119,0.32)",
  transition: "transform .15s ease, box-shadow .25s ease, background .25s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #C9A777 0%, #b4966b 50%, #a1885e 100%)",
    boxShadow: "0 12px 24px rgba(201,167,119,0.40)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "rgba(123,110,102,0.16)",
    color: "rgba(123,110,102,0.55)",
    boxShadow: "none",
  },
  "& .MuiButton-endIcon": { marginLeft: 8 },
}));

/**
 * Scroll to, and focus, the next required field that is still empty.
 * Shared by the footer ring and the ribbon's compact action.
 *
 * @param {string | undefined} name
 */
export const jumpToField = (name) => {
  if (!name) return;
  const field = /** @type {HTMLElement | null} */ (
    document.querySelector(`[name="${name}"]`) || document.getElementById(`f-${name}`)
  );
  if (!field) return;
  // Guarded: not every environment implements it (jsdom does not), and an
  // exception here would skip the focus below — leaving the control looking
  // broken rather than degrading to "focus without the smooth scroll".
  field.scrollIntoView?.({ behavior: "smooth", block: "center" });
  // Focus after the scroll settles; focusing first makes the browser jump.
  window.setTimeout(() => {
    try {
      field.focus({ preventScroll: true });
    } catch {
      /* a non-focusable control (a choice card) still got scrolled to */
    }
  }, 400);
};

/**
 * @param {Object} props
 * @param {string} props.label
 * @param {() => void} props.onClick
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.busy]
 * @param {{done: number, total: number}} [props.progress]  Required-field completion.
 * @param {string} [props.nextIncomplete]  Field name to scroll to, if any.
 * @param {string} [props.next]  Label of the step this action leads to.
 * @param {React.ReactNode} [props.secondary]
 * @param {boolean} [props.pinned]  Fix the bar to the bottom of the viewport.
 * @param {number} [props.maxWidth]  Content width when pinned; matches the page.
 * @param {boolean} [props.railOffset]  Pinned mode: leave the left rail's column
 *        clear from `tablet` up, so the bar spans only the content beside it.
 *        Off for wide layouts, which have no rail.
 * @param {(height: number) => void} [props.onHeightChange]  Measured bar height,
 *        so the shell can reserve exactly that much room beneath the content.
 * @param {(inView: boolean) => void} [props.onInViewChange]  Inline mode only.
 * @param {Object} [props.sx]
 */
const FlowCta = ({
  label = "Continue",
  onClick,
  disabled,
  busy,
  progress,
  nextIncomplete,
  next,
  secondary,
  pinned,
  maxWidth = 1280,
  railOffset,
  onHeightChange,
  onInViewChange,
  sx,
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { rootMargin: "0px 0px -8px 0px", threshold: 0.4 });

  // A pinned bar is always on screen, so there is nothing for the ribbon to
  // stand in for — report it as permanently in view and let the shell drop the
  // ribbon copy rather than show two Continues.
  useEffect(() => {
    onInViewChange?.(pinned ? true : inView);
  }, [inView, pinned, onInViewChange]);

  // Tell the shell the footer is gone when this unmounts, so a ribbon action
  // never lingers on a screen that has no forward action of its own.
  useEffect(() => () => onInViewChange?.(true), [onInViewChange]);

  // The bar overlays the page, so the page has to know how tall it is. It is
  // measured rather than assumed because the height changes with the
  // breakpoint (the row becomes a column on a phone), with the progress ring,
  // and with a wrapped label — a hard-coded reserve is wrong on some screen
  // and silently hides the last field.
  useEffect(() => {
    const node = ref.current;
    if (!pinned || !onHeightChange || !node) return undefined;
    const report = () => onHeightChange(node.offsetHeight);
    report();
    // jsdom and older WebViews have no ResizeObserver; the one measurement
    // above still gives a usable reserve.
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(report);
    observer.observe(node);
    return () => observer.disconnect();
  }, [pinned, onHeightChange]);

  // Reset the reserve when the bar goes away, so a screen without a forward
  // action does not keep a strip of empty space at the bottom.
  useEffect(() => () => onHeightChange?.(0), [onHeightChange]);

  const content = (
    /* `column`, not `column-reverse`: the button is the last thing in the
       stack on a phone, nearest the thumb, and the progress ring reads
       first — "3 left, then Continue". */
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 1.5, sm: 2 }}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
    >
      <Box sx={{ minWidth: 0, display: "flex", justifyContent: { xs: "center", sm: "flex-start" } }}>
        {secondary}
        {!secondary && progress && (
          <StepProgress
            progress={progress}
            canJump={Boolean(nextIncomplete)}
            onJump={() => jumpToField(nextIncomplete)}
            nudge={Boolean(nextIncomplete)}
          />
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ flexShrink: 0, justifyContent: { xs: "stretch", sm: "flex-end" } }}
      >
        {next && (
          <Typography
            sx={{
              display: { xs: "none", sm: "block" },
              color: MUTED,
              fontSize: "0.8125rem",
              whiteSpace: "nowrap",
            }}
          >
            Next up{" "}
            <Box component="span" sx={{ color: INK, fontWeight: 600 }}>
              {next}
            </Box>
          </Typography>
        )}
        <PrimaryAction
          type="button"
          onClick={onClick}
          disabled={disabled || busy}
          endIcon={busy ? null : <ArrowForwardIcon />}
          sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
        >
          {busy ? (
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <CircularProgress size={18} sx={{ color: "inherit" }} />
              <span>Working…</span>
            </Stack>
          ) : (
            label
          )}
        </PrimaryAction>
      </Stack>
    </Stack>
  );

  if (pinned) {
    return (
      // The fixed layer spans the viewport but paints nothing. It exists only
      // to position the bar; `pointerEvents: none` keeps the empty area — which
      // is most of the width once the bar is cropped — from swallowing clicks
      // meant for the page beneath. The bar itself takes them back.
      <Box
        ref={ref}
        component="footer"
        data-testid="flow-cta"
        data-pinned="true"
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          // Above the page and the sticky ribbon (1050), below MUI's modals
          // and the loading overlay, which must still cover it.
          zIndex: 1200,
          pointerEvents: "none",
          pt: { xs: 1, sm: 1.5 },
          // Floats clear of the bottom edge, the way the unpinned header floats
          // clear of the top. The iOS home indicator is added to that rather
          // than replacing it, or the button sits under it on a phone.
          pb: {
            xs: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
            sm: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          },
          ...sx,
        }}
      >
        <Box sx={{ maxWidth, mx: "auto", px: { xs: 3, md: 6 } }}>
          {/* The same container, spacing and column split as JourneyShell's
              body, so the bar lines up with the content beside the rail rather
              than being positioned by a hand-tuned percentage that drifts the
              moment the grid changes. Below `tablet` the rail is hidden and the
              bar takes the full width. */}
          <Grid2 container spacing={{ xs: 3, md: 4 }}>
            {railOffset && (
              <Grid2
                size={{ tablet: 4 }}
                sx={{ display: { xs: "none", tablet: "block" } }}
                data-testid="flow-cta-rail-gap"
                aria-hidden
              />
            )}
            <Grid2 size={{ xs: 12, tablet: railOffset ? 8 : 12 }}>
              <Box
                sx={{
                  pointerEvents: "auto",
                  px: { xs: 2.5, sm: 3 },
                  py: { xs: 1.5, sm: 1.75 },
                  // The pill radius is for the single-row layout; on a phone
                  // the bar stacks into a column and a 999px radius on
                  // something that tall reads as a lozenge rather than a bar.
                  borderRadius: { xs: 4, sm: "999px" },
                  // The header's flat cream was the right family but the wrong
                  // surface here. The header sits on the cream top of the page
                  // gradient and blends into it; this bar floats over the
                  // white bottom of that gradient, where the same cream reads as a
                  // slab laid on the page — and it dulls the gold action, which
                  // is the one thing on the bar that has to carry.
                  //
                  // So: near-white at the top easing into the page's own cream
                  // at the bottom. It reads as a lit surface rather than a
                  // panel, keeps the warmth, and gives the gold button a light
                  // ground to sit on. The hairline moves to the gold family to
                  // tie it to the button instead of outlining it in grey.
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(253,246,234,0.94) 100%)",
                  backdropFilter: "saturate(180%) blur(14px)",
                  WebkitBackdropFilter: "saturate(180%) blur(14px)",
                  border: "1px solid rgba(201,167,119,0.28)",
                  // Two shadows, tinted with the ink rather than grey: a wide
                  // soft one for the float and a tight one for the edge, so it
                  // lifts off white instead of smudging into it.
                  boxShadow:
                    "0 12px 32px rgba(61,50,43,0.12), 0 2px 6px rgba(61,50,43,0.05)",
                }}
              >
                {content}
              </Box>
            </Grid2>
          </Grid2>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      component="footer"
      data-testid="flow-cta"
      sx={{
        mt: { xs: 3, md: 4 },
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 2.5 },
        borderRadius: 4,
        bgcolor: "rgba(255,255,255,0.7)",
        border: "1px solid rgba(201,167,119,0.28)",
        boxShadow: "0 10px 28px rgba(123,110,102,0.08)",
        ...sx,
      }}
    >
      {content}
    </Box>
  );
};

export default FlowCta;
